/**
 * Milestone X4: Explorer API client.
 *
 * Thin wrapper over `/api/explorer/*`. Separate from `fsApi` because the
 * Explorer endpoints carry their own semantics (canonical paths,
 * cardId enrichment, breadcrumbs) that the generic FS shim doesn't.
 *
 * Hot path: TanStack Query caches per canonical path. `gcTime` 5 min,
 * `staleTime` 30 s — matches the chaos workspace cadence (a session
 * writes a file → user refreshes the right pane manually or on focus).
 *
 * Mutations (POST / DELETE) are invalidation-aware: they return a list
 * of cache keys callers should invalidate via TanStack queryClient.
 */
import { env } from "../../env";
import { apiJson } from "./client";

export interface ExplorerEntry {
  name: string;
  path: string;
  isDir: boolean;
  size: number | null;
  modifiedAt: string | null;
  cardId: string | null;
  /**
   * Human-readable alias from the database (org display_name, project
   * name, card title). Only present when the tree was fetched with
   * `view: 'aliased'`. UI should render this when available and fall
   * back to `name` otherwise. Canonical operations still use `name`/
   * `path` — alias is purely cosmetic.
   */
  displayName?: string | null;
  children?: ExplorerEntry[];
  childrenTruncated?: boolean;
}

export type ExplorerViewMode = 'raw' | 'aliased';

export interface ExplorerBreadcrumb {
  name: string;
  path: string;
}

export interface ExplorerTreeResponse {
  path: string;
  entries: ExplorerEntry[];
  breadcrumbs: ExplorerBreadcrumb[];
  truncated: boolean;
  depth: 1 | 2;
}

export interface ExplorerWriteResponse {
  path: string;
  size: number;
  modifiedAt: string;
}

export interface ExplorerMkdirResponse {
  path: string;
  created: boolean;
  modifiedAt: string;
}

export interface ExplorerDeleteResponse {
  path: string;
  removed: boolean;
  wasDirectory: boolean;
}

export interface ExplorerRenameResponse {
  from: string;
  to: string;
  isDirectory: boolean;
  modifiedAt: string;
}

export interface ExplorerStatResponse {
  path: string;
  name: string;
  isDir: boolean;
  kind: "markdown" | "code" | "text" | "image" | "binary" | "dir";
  size: number | null;
  /** Snake_case mirror of `size` for parity with legacy FsTreeNode. */
  size_bytes: number | null;
  mime: string | null;
  modifiedAt: string;
  /** Snake_case mirror of `modifiedAt`. */
  modified_at: string;
  cardId: string | null;
}

/**
 * Mirrors the legacy FsReadResponse shape (snake_case fields) so the
 * preview-pane component can swap /api/fs/read → /api/explorer/read
 * without touching its render logic.
 */
export interface ExplorerReadResponse {
  path: string;
  name: string;
  size_bytes: number;
  bytes_read: number;
  offset_bytes: number;
  eof: boolean;
  mime: string | null;
  kind: "markdown" | "code" | "text" | "image" | "binary";
  text: string;
  masked: boolean;
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  return search.toString();
}

interface TreeOptions {
  /**
   * Canonical directory to list. OMIT it for the Explorer root: the gateway
   * answers with its own canonical root (`/srv/chaos` in production,
   * `/srv/chaos-stg` on staging) and says so in the response's `path`. The UI
   * holds no copy of that string — see useExplorerRoot / explorerApi.root().
   */
  path?: string;
  depth?: 1 | 2;
  includeOrphans?: boolean;
  /**
   * Aliased view enriches entries with `displayName` for orgs/projects/
   * tasks (X6, 2026-05-21). Default 'raw' — canonical names only.
   */
  view?: ExplorerViewMode;
}

export const explorerApi = {
  tree(opts: TreeOptions = {}): Promise<ExplorerTreeResponse> {
    const q = buildQuery({
      path: opts.path,
      depth: opts.depth,
      include_orphans: opts.includeOrphans ? "1" : undefined,
      view: opts.view === 'aliased' ? 'aliased' : undefined,
    });
    return apiJson<ExplorerTreeResponse>(`/explorer/tree?${q}`);
  },

  /**
   * The gateway's Explorer root, learned from the gateway: a depth-1 listing
   * with no `path` is the root, and its `path` is the canonical spelling.
   * (This is the request the Files page used to send with "/srv/chaos"
   * hard-coded, which the staging gateway answered 403.)
   *
   * The answer is the root's own listing, byte for byte what
   * `tree({ path: <root>, depth: 1, view })` returns (the gateway defaults the
   * path to its root) - so `rootListing` hands the whole response back and the
   * caller can seed the tree cache with it instead of asking twice.
   */
  rootListing(view?: ExplorerViewMode): Promise<ExplorerTreeResponse> {
    return explorerApi.tree({ depth: 1, view });
  },

  async root(view?: ExplorerViewMode): Promise<string> {
    const r = await explorerApi.rootListing(view);
    return r.path;
  },

  /** Build a URL for `<img src>` / `<a download>` / fetch(). */
  fileUrl(p: string, disposition: "inline" | "attachment" = "inline"): string {
    const base = env.VITE_API_BASE_URL.replace(/\/$/, "");
    const q = buildQuery({ path: p, disposition });
    return `${base}/explorer/file?${q}`;
  },

  /** Convenience URL for the dedicated download endpoint. */
  downloadUrl(p: string): string {
    const base = env.VITE_API_BASE_URL.replace(/\/$/, "");
    const q = buildQuery({ path: p });
    return `${base}/explorer/download?${q}`;
  },

  /**
   * Write UTF-8 text to <path>. Creates parent dirs, chown 1001:1001.
   * 5 MB cap on content (matches backend MAX_WRITE_BYTES).
   */
  writeFile(path: string, content: string): Promise<ExplorerWriteResponse> {
    return apiJson<ExplorerWriteResponse>("/explorer/file", {
      method: "POST",
      json: { path, content },
    });
  },

  mkdir(path: string): Promise<ExplorerMkdirResponse> {
    return apiJson<ExplorerMkdirResponse>("/explorer/mkdir", {
      method: "POST",
      json: { path },
    });
  },

  /**
   * Delete file or directory. `recursive` MUST be opt-in; default false
   * matches the server contract that only empty dirs and files can be
   * removed without an explicit confirmation.
   */
  deletePath(path: string, recursive = false): Promise<ExplorerDeleteResponse> {
    const q = buildQuery({ path, recursive: recursive ? "1" : "0" });
    return apiJson<ExplorerDeleteResponse>(`/explorer/path?${q}`, {
      method: "DELETE",
    });
  },

  /**
   * Stat a single canonical path. Metadata only — no body. Used by the
   * preview pane to decide which viewer to render before fetching
   * content.
   */
  stat(path: string): Promise<ExplorerStatResponse> {
    const q = buildQuery({ path });
    return apiJson<ExplorerStatResponse>(`/explorer/stat?${q}`);
  },

  /**
   * Slice-read a text-ish file. Returns the legacy FsReadResponse shape
   * (snake_case fields). For binary / image kinds the `text` field is
   * empty and the caller should fall back to /api/explorer/download.
   */
  read(opts: { path: string; offset?: number; limit?: number }): Promise<ExplorerReadResponse> {
    const q = buildQuery({
      path: opts.path,
      offset: opts.offset,
      limit: opts.limit,
    });
    return apiJson<ExplorerReadResponse>(`/explorer/read?${q}`);
  },

  rename(from: string, to: string): Promise<ExplorerRenameResponse> {
    return apiJson<ExplorerRenameResponse>("/explorer/rename", {
      method: "POST",
      json: { from, to },
    });
  },
};

export const explorerKeys = {
  all: ["explorer"] as const,
  // NOT under `all`: the root is a fact about the gateway ("/srv/chaos" in
  // production), fixed for as long as it runs. Under `all` it was refetched by
  // the page's Refresh and by every create / rename / delete / save, each of
  // which invalidates `all` - and a refetch that failed took the whole Files
  // page down although the root was sitting in the cache. Nothing a person
  // does in the Explorer can change the root, so nothing invalidates it.
  root: ["explorer-root"] as const,
  // The 4th element is the view mode so raw and aliased fetches stay
  // separately cached. Without it, switching the toggle would silently
  // serve stale entries from the other mode until refetch.
  tree: (p: string, depth: 1 | 2, view: ExplorerViewMode = 'raw') =>
    [...explorerKeys.all, "tree", p, depth, view] as const,
  stat: (p: string) => [...explorerKeys.all, "stat", p] as const,
  read: (p: string, offset: number, limit: number) =>
    [...explorerKeys.all, "read", p, offset, limit] as const,
};

/**
 * Helpers for cache invalidation after a mutation. We invalidate two
 * scopes:
 *   1. the directory containing the affected path (so the tree node
 *      that owns this listing refreshes its children); and
 *   2. the entry's own path (so a freshly-saved file's preview reads
 *      the new size/mtime).
 */
export function parentOf(canonical: string): string {
  // /srv/chaos/.../foo.txt -> /srv/chaos/.../
  const idx = canonical.lastIndexOf("/");
  if (idx <= 0) return canonical;
  return canonical.slice(0, idx);
}
